import { useState } from "react";
import { Upload, Loader2, Save, Camera, Fingerprint, Tag, Layers, ThumbsUp, ThumbsDown, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, compressImage, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { PossibleVariants } from "@/components/app/PossibleVariants";
import { ZoomablePreview } from "@/components/app/ZoomablePreview";

const MAKE_OPTIONS = [
  "", "Chevrolet", "Pontiac", "Buick", "Oldsmobile", "Cadillac",
  "Ford", "Mercury", "Lincoln", "Plymouth", "Dodge", "Chrysler",
  "AMC", "Jaguar", "MG", "Triumph", "Porsche", "Volkswagen", "Other",
];

const Dropzone = ({ testid, preview, onFile, onCrop, cropLabel, label, hint }) => (
  <label data-testid={`${testid}-dropzone`} className="block border-2 border-dashed border-shop-strong hover:border-[var(--accent-amber)] transition-colors cursor-pointer p-4 text-center min-h-[130px] flex flex-col items-center justify-center">
    <input data-testid={testid} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
    {preview ? (
      <ZoomablePreview
        src={preview}
        alt={label}
        testid={`${testid}-preview`}
        className="max-h-32"
        onCrop={onCrop}
        cropLabel={cropLabel || "Use this crop"}
      />
    ) : (
      <div className="text-secondary">
        <Upload size={22} className="mx-auto text-amber-brass mb-1" />
        <p className="font-display uppercase tracking-widest text-cream text-sm">{label}</p>
        <p className="text-[10px] font-mono mt-0.5">{hint}</p>
      </div>
    )}
  </label>
);

const CombinedID = () => {
  const [carFile, setCarFile] = useState(null);
  const [carPreview, setCarPreview] = useState(null);
  const [vinFile, setVinFile] = useState(null);
  const [vinPreview, setVinPreview] = useState(null);
  const [tagFile, setTagFile] = useState(null);
  const [tagPreview, setTagPreview] = useState(null);

  const [vin, setVin] = useState("");
  const [tagText, setTagText] = useState("");
  const [make, setMake] = useState("");
  const [ownerNotes, setOwnerNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [feedback, setFeedback] = useState(null); // "up" | "down" | null
  const [correction, setCorrection] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState(new Set());
  const toggleVariant = (name) => setSelectedVariants((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const [ocrVin, setOcrVin] = useState(false);
  const [ocrTag, setOcrTag] = useState(false);

  const bindFile = (setF, setP, onLoaded) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setF(f);
    setP(URL.createObjectURL(f));
    if (onLoaded) await onLoaded(f);
  };

  const readVinFromPhoto = async (f) => {
    setOcrVin(true);
    try {
      const { base64, mime } = await compressImage(f, { maxDim: 1600, quality: 0.85 });
      const { data } = await api.post("/vehicles/read-vin-photo", {
        image_base64: base64, mime_type: mime, auto_decode: false,
      });
      const readVin = (data.vin || "").trim();
      if (readVin) {
        setVin(readVin);
        toast.success(`VIN read: ${readVin} — edit if needed`);
      } else {
        toast.error("Couldn't read a VIN from that photo — try again with better lighting.");
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setOcrVin(false);
    }
  };

  const readTagFromPhoto = async (f) => {
    setOcrTag(true);
    try {
      const { base64, mime } = await compressImage(f, { maxDim: 1600, quality: 0.85 });
      const { data } = await api.post("/vehicles/read-tag-photo", {
        image_base64: base64, mime_type: mime,
      });
      const read = data.reading || {};
      if (read.tag_text) {
        setTagText(read.tag_text);
        if (read.make_guess && !make) setMake(read.make_guess);
        toast.success("Tag text pre-filled — edit if needed");
      } else {
        toast.error("Couldn't read tag text from that photo.");
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setOcrTag(false);
    }
  };

  // Aggregate every reason the result might be untrustworthy
  const collectWarnings = (data) => {
    if (!data?.unified) return [];
    const u = data.unified;
    const s = data.sources || {};
    const w = [];

    if (u.confidence === "low") {
      w.push({
        severity: "high",
        title: "Low overall confidence",
        detail: "The unified identification could not be corroborated across sources. Treat every field as tentative until you verify with a build sheet, Marti Report, or in-person inspection.",
      });
    }

    (u.vetting_notes || []).forEach((n) => {
      w.push({
        severity: "high",
        title: "Variant claim rejected",
        detail: n,
      });
    });

    (u.discrepancies || []).forEach((d) => {
      w.push({
        severity: "medium",
        title: `Sources disagree on ${d.field}`,
        detail: `${Object.entries(d.sources || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}. ${d.resolution || ""}`,
      });
    });

    // Photo-quality warnings from sub-decoders
    const photoConf = s.photo_identification?.confidence;
    if (photoConf === "low") {
      w.push({
        severity: "medium",
        title: "Photo identification confidence is low",
        detail: "The car photo is too small, too dark, taken from a bad angle, or obscured. Try a well-lit 3/4 front shot showing the front fascia, side profile, and any badges.",
      });
    }
    const vinConf = s.vin_photo_reading?.vin_confidence;
    if (vinConf === "low") {
      w.push({
        severity: "medium",
        title: "VIN plate is hard to read",
        detail: "Characters on the VIN photo are worn or blurry. Try again with better lighting, straight-on angle, and get closer so the VIN fills most of the frame.",
      });
    }
    const vinAmbig = s.vin_photo_reading?.ambiguous_chars || [];
    if (vinAmbig.length) {
      w.push({
        severity: "medium",
        title: "Ambiguous VIN characters",
        detail: `Some VIN characters could be read multiple ways: ${vinAmbig.join(", ")}. Verify each against the dashboard plate directly.`,
      });
    }
    const tagAmbig = s.tag_decoded?.ambiguous_chars || [];
    if (tagAmbig.length) {
      w.push({
        severity: "low",
        title: "Ambiguous tag characters",
        detail: `Some tag characters were unclear: ${tagAmbig.join(", ")}. Edit the pre-filled tag text if needed and re-decode.`,
      });
    }
    const tagUnknown = s.tag_decoded?.unknown_codes || [];
    if (tagUnknown.length) {
      w.push({
        severity: "low",
        title: "Unknown tag codes",
        detail: `Some tag codes weren't in the factory reference tables: ${tagUnknown.join(", ")}. These may be aftermarket, dealer-installed, or from a year/make our tables don't yet cover.`,
      });
    }

    if (!s.photo_identification && !s.vin_decoded && !s.tag_decoded) {
      w.push({
        severity: "high",
        title: "No sources returned data",
        detail: "None of the sub-decoders (photo, VIN, tag) returned usable data. Check your inputs and try again.",
      });
    } else if ((u.sources_used || []).length < 2) {
      w.push({
        severity: "low",
        title: "Only one source used",
        detail: "For maximum accuracy, provide at least two of {car photo, VIN, tag}. Multi-source cross-referencing catches clone builds and misreads.",
      });
    }

    return w;
  };

  const warnings = collectWarnings(result);
  const hasHighSeverity = warnings.some((w) => w.severity === "high");

  const sendFeedback = async (isCorrect, corr = "") => {
    if (!result) return;
    try {
      await api.post("/vehicles/feedback", {
        endpoint: "combined-id",
        request_payload: {
          vin, make, tag_text: tagText, notes_from_owner: ownerNotes,
          car_photo: !!carFile, vin_photo: !!vinFile, tag_photo: !!tagFile,
        },
        response_snapshot: result.unified,
        is_correct: isCorrect,
        correction: corr || null,
      });
      setFeedbackSent(true);
      toast.success(isCorrect ? "Thanks — noted as correct!" : "Thanks — we'll use this to improve.");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    }
  };

  const run = async () => {
    if (!carFile && !vinFile && !tagFile && !vin.trim() && !tagText.trim()) {
      toast.error("Provide at least one input — a photo, VIN, or tag");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        make: make || null,
        notes_from_owner: ownerNotes || null,
        vin: vin.trim() || null,
        tag_text: tagText || null,
      };
      if (carFile) payload.car_photo_base64 = (await compressImage(carFile, { maxDim: 1400, quality: 0.82 })).base64;
      if (vinFile) payload.vin_photo_base64 = (await compressImage(vinFile, { maxDim: 1600, quality: 0.85 })).base64;
      if (tagFile) payload.tag_photo_base64 = (await compressImage(tagFile, { maxDim: 1600, quality: 0.85 })).base64;

      const { data } = await api.post("/vehicles/combined-id", payload);
      setResult(data);
      setFeedback(null);
      setCorrection("");
      setFeedbackSent(false);
      // Pop the warning dialog if the result has any high-severity concerns
      const initialWarnings = collectWarnings(data);
      if (initialWarnings.some((w) => w.severity === "high")) {
        setWarningOpen(true);
      }
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!result?.unified) return;
    setSaving(true);
    const u = result.unified;
    try {
      await api.post("/vehicles/garage", {
        year: u.year || "",
        make: u.make || "",
        model: u.model || "",
        trim: u.trim || u.special_variant || "",
        vin: result.vin_used || undefined,
        photo_base64: carFile ? (await compressImage(carFile, { maxDim: 1200, quality: 0.8 })).base64 : undefined,
        photo_mime: carFile ? "image/jpeg" : undefined,
        vin_decoded: result.sources?.vin_decoded || undefined,
        photo_identified: result.sources?.photo_identification || undefined,
        tag_decoded: result.sources?.tag_decoded || undefined,
        notes: u.unified_summary || undefined,
        confirmed_options: [...selectedVariants],
      });
      toast.success("Filed in your garage");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const u = result?.unified || {};

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Station 00</p>
        <h1 className="font-display text-5xl uppercase text-cream mt-1">Combined ID</h1>
        <p className="text-secondary mt-2 max-w-3xl">
          Give us anything you&apos;ve got — a car photo, a VIN, a data plate — one or all three.
          The more you provide, the more the AI can cross-reference to nail the ID.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* CAR PHOTO */}
        <div className="panel p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="tag-chip">01 · Car Photo</span>
            <Camera size={14} className="text-amber-brass" />
          </div>
          <Dropzone
            testid="combo-car-input"
            preview={carPreview}
            onFile={bindFile(setCarFile, setCarPreview)}
            onCrop={(f) => { setCarFile(f); setCarPreview(URL.createObjectURL(f)); }}
            cropLabel="Use this crop"
            label="Upload car photo"
            hint="Full body or 3/4 view"
          />
        </div>

        {/* VIN */}
        <div className="panel p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="tag-chip">02 · VIN</span>
            <Fingerprint size={14} className="text-amber-brass" />
          </div>
          <input
            data-testid="combo-vin-input"
            className="data-input uppercase tracking-widest"
            placeholder="Type VIN…"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase().replace(/\s/g, ""))}
            maxLength={17}
          />
          <div className="text-center font-mono text-[10px] text-secondary">— or —</div>
          <div>
            <Dropzone
              testid="combo-vin-photo-input"
              preview={vinPreview}
              onFile={bindFile(setVinFile, setVinPreview, readVinFromPhoto)}
              onCrop={async (f) => { setVinFile(f); setVinPreview(URL.createObjectURL(f)); await readVinFromPhoto(f); }}
              cropLabel="Re-read this crop"
              label="Photo of VIN"
              hint="Dash plate / door jamb"
            />
            {ocrVin && (
              <div className="mt-1 font-mono text-[10px] uppercase text-amber-brass flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Reading VIN from photo…
              </div>
            )}
          </div>
        </div>

        {/* TAG */}
        <div className="panel p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="tag-chip">03 · Data Tag</span>
            <Tag size={14} className="text-amber-brass" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-[10px] uppercase tracking-widest text-secondary">Type tag codes</label>
              {ocrTag && (
                <span className="font-mono text-[10px] uppercase text-amber-brass flex items-center gap-1.5">
                  <Loader2 size={11} className="animate-spin" /> Reading tag…
                </span>
              )}
            </div>
            <textarea
              data-testid="combo-tag-text"
              rows={3}
              className="data-input font-mono mt-1"
              placeholder="Cowl / door / fender tag codes…"
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              disabled={ocrTag}
            />
          </div>
          <Dropzone
            testid="combo-tag-photo-input"
            preview={tagPreview}
            onFile={bindFile(setTagFile, setTagPreview, readTagFromPhoto)}
            onCrop={async (f) => { setTagFile(f); setTagPreview(URL.createObjectURL(f)); await readTagFromPhoto(f); }}
            cropLabel="Re-read this crop"
            label="Photo of tag"
            hint="Cowl / door / fender"
          />
        </div>
      </div>

      {/* Shared context */}
      <div className="panel p-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-secondary">Make (Optional Hint)</label>
          <select
            data-testid="combo-make-select"
            className="data-input mt-1"
            value={make}
            onChange={(e) => setMake(e.target.value)}
          >
            {MAKE_OPTIONS.map((m) => (
              <option key={m} value={m}>{m === "" ? "— Let AI guess —" : m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-secondary">
            Owner Notes (Badges, options, plate details the AI can&apos;t see)
          </label>
          <textarea
            data-testid="combo-owner-notes"
            className="data-input mt-1 font-body"
            rows={2}
            placeholder="e.g. Has GT badges, fog lamps, dual exhaust, GT gas cap. Door plate shows order code Y."
            value={ownerNotes}
            onChange={(e) => setOwnerNotes(e.target.value)}
          />
        </div>
      </div>

      <button
        data-testid="combo-run-btn"
        onClick={run}
        disabled={loading}
        className="chrome-btn w-full flex items-center justify-center gap-2 text-lg py-4"
      >
        {loading ? <><Loader2 size={20} className="animate-spin" /> Cross-Referencing…</> : <><Layers size={20} /> Identify Vehicle</>}
      </button>

      {result && (
        <div data-testid="combo-result" className="space-y-5">
          {/* Warning banner — always visible if any concerns */}
          {warnings.length > 0 && (
            <div
              data-testid="warning-banner"
              className={`panel p-5 border-l-4 ${hasHighSeverity ? "border-[var(--accent-red)]" : "border-[var(--accent-amber)]"}`}
              style={{ background: hasHighSeverity ? "rgba(217,32,39,0.06)" : "rgba(245,197,24,0.06)" }}
            >
              <div className="flex items-start gap-3">
                <AlertTriangle
                  size={22}
                  className={hasHighSeverity ? "text-red-candy shrink-0" : "text-amber-brass shrink-0"}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-display uppercase text-xl text-cream leading-tight">
                      {hasHighSeverity ? "Trust This Result Carefully" : "Heads Up"}
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                      {warnings.length} concern{warnings.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <p className="text-secondary text-sm mt-1">
                    {hasHighSeverity
                      ? "One or more critical issues were detected. Read below before treating any of this as authoritative."
                      : "A few minor issues — read them to sharpen the decode."}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {warnings.map((w, i) => (
                      <li key={i} className="text-sm text-cream leading-relaxed">
                        <span className={`font-mono mr-2 ${w.severity === "high" ? "text-red-candy" : w.severity === "medium" ? "text-amber-brass" : "text-secondary"}`}>
                          {w.severity === "high" ? "✗" : w.severity === "medium" ? "!" : "·"}
                        </span>
                        <span className="font-display uppercase text-sm text-cream mr-2">{w.title}.</span>
                        <span className="text-secondary">{w.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Unified header */}
          <div className="panel p-6 md:p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-[3px] w-24 bg-[var(--accent-red)]" />
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Unified ID</p>
                <div className="font-display text-5xl uppercase text-cream leading-none mt-2">
                  {u.year} {u.make}
                </div>
                <div className="font-display text-3xl uppercase text-red-candy leading-tight mt-1">
                  {u.model} {u.trim && <span className="text-amber-brass">— {u.trim}</span>}
                </div>
                <div className="font-mono text-xs text-secondary mt-2">
                  {u.body_style}
                  {u.engine && <> · {u.engine}</>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {u.confidence && (
                  <span className="font-mono text-xs uppercase text-amber-brass">
                    Confidence: {u.confidence}
                  </span>
                )}
                <div className="flex flex-wrap gap-1 justify-end">
                  {(u.sources_used || []).map((s) => (
                    <span key={s} className="tag-chip">{s}</span>
                  ))}
                </div>
                <button
                  data-testid="combo-save-btn"
                  onClick={save}
                  disabled={saving}
                  className="ghost-btn flex items-center gap-2 mt-2"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save to Garage
                </button>
              </div>
            </div>

            {u.special_variant && (
              <div className="mt-4 border-l-4 border-[var(--accent-red)] bg-[rgba(217,32,39,0.08)] px-4 py-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-red-candy">
                      Special Variant
                      {u.variant_confidence === "corroborated" && " · Confirmed by multiple sources"}
                      {u.variant_confidence === "single-source" && ` · Single-source claim (${u.variant_source || "unknown"})`}
                      {u.variant_confidence === "owner-declared" && " · Declared by owner"}
                    </div>
                    <div className="font-display text-2xl uppercase text-cream mt-0.5">{u.special_variant}</div>
                  </div>
                  {u.variant_confidence !== "corroborated" && (
                    <button
                      data-testid="reject-variant-btn"
                      onClick={() => setResult({ ...result, unified: { ...u, special_variant: "", variant_source: null, variant_confidence: null, _user_rejected: u.special_variant } })}
                      className="ghost-btn text-[10px] flex items-center gap-1"
                      title="Reject this variant claim"
                    >
                      Not This Variant
                    </button>
                  )}
                </div>
                {u.variant_confidence === "single-source" && (
                  <p className="text-[11px] text-secondary mt-2 italic">
                    Only one source claimed this variant. Verify with the door plate, build sheet, or Marti Report before treating it as authentic.
                  </p>
                )}
              </div>
            )}

            {u.unified_summary && (
              <p className="mt-5 text-cream leading-relaxed whitespace-pre-wrap">{u.unified_summary}</p>
            )}

            {(u.build_date || u.assembly_plant || u.colors) && (
              <div className="mt-5 grid sm:grid-cols-3 gap-4">
                {u.build_date && (
                  <div>
                    <div className="font-mono text-[10px] uppercase text-secondary">Build Date</div>
                    <div className="font-mono text-sm text-cream">{u.build_date}</div>
                  </div>
                )}
                {u.assembly_plant && (
                  <div>
                    <div className="font-mono text-[10px] uppercase text-secondary">Assembly Plant</div>
                    <div className="font-mono text-sm text-cream">{u.assembly_plant}</div>
                  </div>
                )}
                {u.colors && (
                  <div>
                    <div className="font-mono text-[10px] uppercase text-secondary">Colors</div>
                    <div className="font-mono text-sm text-cream">{u.colors}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vetting notes — variant claims rejected by cross-check */}
          {u.vetting_notes?.length > 0 && (
            <div className="panel p-5" data-testid="vetting-notes">
              <span className="tag-chip" style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}>
                Variant Claim Rejected
              </span>
              <div className="mt-2 space-y-2">
                {u.vetting_notes.map((n, i) => (
                  <p key={i} className="text-sm text-cream leading-relaxed">
                    <span className="text-red-candy font-mono mr-1">✗</span>
                    {n}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Possible variants */}
          {u.possible_variants?.length > 0 && (
            <div className="panel p-5">
              <span className="tag-chip">Possible Option Packages</span>
              <PossibleVariants
                variants={u.possible_variants}
                title=""
                subtitle="Not confirmed but available for this model+year. Tap one to see how to confirm."
                selected={selectedVariants}
                onToggleSelect={toggleVariant}
              />
            </div>
          )}

          {/* Discrepancies */}
          {u.discrepancies?.length > 0 && (
            <div className="panel p-5">
              <span className="tag-chip" style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}>
                Cross-Reference Conflicts
              </span>
              <div className="mt-3 space-y-3">
                {u.discrepancies.map((d, i) => (
                  <div key={i} className="border border-shop bg-elevated p-3">
                    <div className="font-mono text-xs uppercase text-red-candy">{d.field}</div>
                    <div className="mt-1 text-xs text-secondary space-y-0.5">
                      {d.sources && Object.entries(d.sources).map(([k, v]) => (
                        <div key={k}>
                          <span className="font-mono text-amber-brass">{k}:</span> {v}
                        </div>
                      ))}
                    </div>
                    {d.resolution && (
                      <div className="mt-2 text-sm text-cream">
                        <span className="font-mono text-[10px] uppercase text-amber-brass">Resolution: </span>
                        {d.resolution}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {u.authentication_tips && (
            <div className="panel p-5">
              <span className="tag-chip">Authentication Tips</span>
              <p className="mt-2 text-sm text-cream leading-relaxed whitespace-pre-wrap">{u.authentication_tips}</p>
            </div>
          )}

          {/* Sub-source details (collapsible) */}
          <details className="panel p-5">
            <summary className="cursor-pointer font-mono text-xs uppercase text-amber-brass tracking-widest">
              Show Individual Source Decodings
            </summary>
            <pre className="mt-3 text-[11px] text-secondary whitespace-pre-wrap overflow-x-auto max-h-[500px]">
              {JSON.stringify(result.sources, null, 2)}
            </pre>
          </details>

          {/* Feedback capture — the learning loop */}
          <div className="panel p-5" data-testid="feedback-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <span className="tag-chip">Help the app learn</span>
                <p className="text-sm text-cream mt-2">
                  Was this identification accurate? Your feedback trains future decodings.
                </p>
              </div>
              {feedbackSent ? (
                <span className="font-mono text-xs uppercase text-amber-brass">Feedback received — thanks!</span>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    data-testid="feedback-up-btn"
                    onClick={() => { setFeedback("up"); sendFeedback(true); }}
                    className={`ghost-btn flex items-center gap-1.5 ${feedback === "up" ? "border-[var(--accent-amber)] text-amber-brass" : ""}`}
                    aria-label="correct"
                  >
                    <ThumbsUp size={14} /> Correct
                  </button>
                  <button
                    data-testid="feedback-down-btn"
                    onClick={() => setFeedback("down")}
                    className={`ghost-btn flex items-center gap-1.5 ${feedback === "down" ? "border-[var(--accent-red)] text-red-candy" : ""}`}
                    aria-label="incorrect"
                  >
                    <ThumbsDown size={14} /> Not Quite
                  </button>
                </div>
              )}
            </div>
            {feedback === "down" && !feedbackSent && (
              <div className="mt-4 space-y-2">
                <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                  What&apos;s the correct answer? (helps us learn)
                </label>
                <textarea
                  data-testid="feedback-correction-input"
                  className="data-input font-body"
                  rows={2}
                  placeholder="e.g. It's a real 1966 GT-350 K-code, not a base Mustang. Or: paint code 63 was Cranberry Red, not Autumn Gold."
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                />
                <button
                  data-testid="feedback-submit-correction"
                  onClick={() => sendFeedback(false, correction)}
                  disabled={!correction.trim()}
                  className="chrome-btn"
                >
                  Submit Correction
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auto-popup warning dialog — appears once when result has high-severity concerns */}
      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogContent data-testid="warning-dialog" className="bg-panel border-shop-strong text-cream max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl text-cream flex items-center gap-2">
              <AlertTriangle className="text-red-candy" size={22} />
              Read Before You Trust This
            </DialogTitle>
            <DialogDescription className="text-secondary text-sm">
              We caught {warnings.length} issue{warnings.length === 1 ? "" : "s"} with this identification. Here&apos;s what to know before you rely on the answer:
            </DialogDescription>
          </DialogHeader>
          <ul className="mt-2 space-y-3 max-h-[50vh] overflow-y-auto pr-2">
            {warnings.map((w, i) => (
              <li key={i} className="border-l-2 pl-3" style={{ borderColor: w.severity === "high" ? "var(--accent-red)" : w.severity === "medium" ? "var(--accent-amber)" : "var(--border-strong)" }}>
                <div className="font-display uppercase text-sm text-cream">{w.title}</div>
                <div className="text-xs text-secondary mt-1 leading-relaxed">{w.detail}</div>
              </li>
            ))}
          </ul>
          <DialogFooter className="mt-4">
            <button
              data-testid="warning-dialog-close"
              onClick={() => setWarningOpen(false)}
              className="chrome-btn"
            >
              Got it — Show Results
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CombinedID;
